import React, { useState, useRef, useEffect, useCallback } from 'react';
import Chevron from '../Chevron/Chevron';
import {
  DropdownContainer,
  DropdownHeader,
  DropdownHeaderContent,
  IconContainer,
  IconImage,
  DropdownText,
  ChevronIcon,
  DropdownListContainer,
  DropdownList,
  DropdownItem,
  DropdownItemText,
  DropdownLabel,
  DropdownPlaceholder,
  DropdownItemsContainer,
  DropdownDivider,
  DropdownItemContent,
  ScrollHint
} from './Dropdown.styles';
import { BLUE, WHITE } from '../../styles/colors';
import ChecklistIcon from '../ChecklistIcon/ChecklistIcon';
import { INTER } from '../../styles/fonts';

export type OptionType = {
  id?: string;
  code?: string;
  name?: string;
  display_name?: string;
  provider?: string;
  icon_url?: string;
  iconUrl?: string;
};

export type DropdownProps<T extends OptionType = OptionType> = {
  label?: string;
  placeholder?: string;
  defaultPlaceholder: string;
  options: T[];
  value?: T | null;
  onChange: (option: T) => void;
  disabled?: boolean;
  outline?: boolean;
  usePortal?: boolean; // Whether to position the dropdown absolutely to the viewport
  selectedOptionFontFamily?: string;
  selectedOptionFontSize?: string;
  showScrollHint?: boolean; // Shows a floating arrow hint while more options remain below the fold
};

const Dropdown = <T extends OptionType = OptionType>({
  label,
  placeholder,
  defaultPlaceholder,
  options,
  value,
  onChange,
  disabled = false,
  outline = true,
  usePortal = false,
  selectedOptionFontFamily = INTER.regular,
  selectedOptionFontSize = '13px',
  showScrollHint = false
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const updateScrollHint = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    setHasMoreBelow(list.scrollHeight - list.scrollTop - list.clientHeight > 4);
  }, []);

  useEffect(() => {
    if (isOpen && showScrollHint) {
      updateScrollHint();
    }
  }, [isOpen, options, showScrollHint, updateScrollHint]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: T) => {
    onChange(option);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get display name for the selected option
  const getDisplayName = (option: T) => {
    return option.display_name || option.name || option.provider || '';
  };

  // Get icon URL for the option
  const getIconUrl = (option: T) => {
    return option.icon_url || option.iconUrl || '';
  };

  // Check if an option is the currently selected one
  const isOptionSelected = (option: T) => {
    if (!value) return false;

    // Try to match by different possible identifiers
    return (
      (option.id && value.id && option.id === value.id) ||
      (option.code && value.code && option.code === value.code) ||
      (option.provider && value.provider && option.provider === value.provider)
    );
  };

  // Create dropdown list content
  const dropdownListContent = (
    <DropdownList ref={listRef} onScroll={showScrollHint ? updateScrollHint : undefined}>
      <DropdownItemsContainer>
        {options.map((option, index) => (
          <React.Fragment key={option.id || option.code || index}>
            <DropdownItem onClick={() => handleOptionClick(option)}>
              <DropdownItemContent>
                {getIconUrl(option) && (
                  <IconContainer>
                    <IconImage src={getIconUrl(option)} alt={getDisplayName(option)} />
                  </IconContainer>
                )}
                <DropdownItemText>
                  {getDisplayName(option)}
                </DropdownItemText>
              </DropdownItemContent>
              {isOptionSelected(option) && <ChecklistIcon size={16} strokeWidth={1.5} />}
            </DropdownItem>
            {index !== options.length - 1 && <DropdownDivider />}
          </React.Fragment>
        ))}
      </DropdownItemsContainer>
    </DropdownList>
  );

  // Calculate position for absolute dropdown
  const getDropdownPosition = () => {
    if (!dropdownRef.current) return {};

    const rect = dropdownRef.current.getBoundingClientRect();
    return {
      position: 'fixed' as const,
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 1000
    };
  };

  // Apply position to dropdown list when it's open and usePortal is true
  useEffect(() => {
    if (isOpen && usePortal && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const listContainer = document.querySelector('.portal-dropdown-list') as HTMLElement;

      if (listContainer) {
        listContainer.style.position = 'fixed';
        listContainer.style.top = `${rect.bottom + 8}px`;
        listContainer.style.left = `${rect.left}px`;
        listContainer.style.width = `${rect.width}px`;
        listContainer.style.zIndex = '1000';
      }
    }
  }, [isOpen, usePortal]);

  return (
    <DropdownContainer ref={dropdownRef}>
      {label && <DropdownLabel style={{ marginBottom: outline ? 8 : 0 }}>{label}</DropdownLabel>}
      <DropdownHeader onClick={toggleDropdown} $outline={outline}>
        {value ? (
          <DropdownHeaderContent>
            {getIconUrl(value) && (
              <IconContainer>
                <IconImage src={getIconUrl(value)} alt={getDisplayName(value)} />
              </IconContainer>
            )}
            <DropdownText
              style={{
                fontFamily: selectedOptionFontFamily,
                fontSize: selectedOptionFontSize
              }}
            >
              {getDisplayName(value)}
            </DropdownText>
          </DropdownHeaderContent>
        ) : (
          <DropdownPlaceholder>{placeholder || defaultPlaceholder}</DropdownPlaceholder>
        )}
        {!disabled && <ChevronIcon $isOpen={isOpen}>
          <Chevron
            direction="down"
            size={10}
            color={BLUE}
            strokeWidth={1}
          />
        </ChevronIcon>}
      </DropdownHeader>

      <DropdownListContainer
        $isOpen={isOpen}
        className={usePortal ? 'portal-dropdown-list' : ''}
        style={usePortal && isOpen ? getDropdownPosition() : undefined}
      >
        {dropdownListContent}
        {showScrollHint && (
          <ScrollHint $visible={hasMoreBelow} aria-hidden="true">
            <Chevron
              direction="down"
              size={10}
              color={WHITE}
              strokeWidth={1.5}
            />
          </ScrollHint>
        )}
      </DropdownListContainer>
    </DropdownContainer>
  );
};

export default Dropdown;
